<template>
  <div>
    <AppNavbar />
    <div class="container-fluid mt-4 px-3 px-xl-4">
      <AppHeader title="Gestión de Pedidos">
        <template #buttons>
          <button type="button" class="btn btn-success" @click="iniciarNuevoPedido">
            Nuevo Pedido
          </button>
        </template>
      </AppHeader>

      <div class="orders-mobile-context card border-0 shadow-sm d-lg-none mb-3">
        <div class="card-body py-3">
          <div class="orders-mobile-context__eyebrow">Pedidos</div>
          <div class="orders-mobile-context__title">Lista y edición en una sola pantalla</div>
          <div class="orders-mobile-context__subtitle">
            Revisá pedidos, retomalos y completalos para enviarlos a caja sin depender de tablas anchas.
          </div>
        </div>
      </div>

      <!-- Pedidos web pendientes de importar (RF-7 de
           SPEC_ECOMMERCE_CUENTAS_v0.1.md): aviso corto con enlace a la
           subsección paginada Pedidos > E-Pedidos (Ampliación 2026-07-30),
           no una lista inline acá. -->
      <div v-if="canManageEcommerce && pedidosWebPendientesCount" class="alert alert-warning d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <span>
          Hay {{ pedidosWebPendientesCount }} pedido(s) web pendiente(s) de atender.
        </span>
        <router-link class="btn btn-dark btn-sm" to="/gestion-pedidos/e-pedidos">
          Ver E-Pedidos
        </router-link>
      </div>

      <div class="row g-4 mt-1">
        <div class="col-xl-7">
          <AppFilter
            v-model="searchInput"
            placeholder="Buscar por ID de pedido, nombre del cliente o estado..."
            customClasses="mb-3"
          />

          <div class="card shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h2 class="h5 mb-0">Lista de Pedidos</h2>
                <small class="text-muted">{{ totalPedidos }} pedidos</small>
              </div>

              <div class="orders-mobile-list d-lg-none">
                <div v-if="cargando" class="card border-0 bg-light">
                  <div class="card-body text-center py-4 text-muted">Cargando pedidos...</div>
                </div>

                <div v-else-if="!pedidos.length" class="card border-0 bg-light">
                  <div class="card-body text-center py-4 text-muted">No se encontraron pedidos.</div>
                </div>

                <div
                  v-for="pedido in pedidos"
                  :key="`mobile-${pedido.pedido_id}`"
                  class="card shadow-sm border-0 orders-mobile-card"
                >
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-start gap-3 mb-3">
                      <div>
                        <div class="orders-mobile-card__eyebrow">Pedido #{{ pedido.pedido_id }}</div>
                        <h3 class="orders-mobile-card__title mb-1">{{ pedido.nombre_cliente || 'Cliente sin nombre' }}</h3>
                        <div class="orders-mobile-card__meta">{{ formatearFecha(pedido.actualizado_en) }}</div>
                      </div>
                      <span :class="['badge', badgeClass(pedido.estado)]">
                        {{ pedido.estado || '—' }}
                      </span>
                    </div>

                    <div class="orders-mobile-card__summary">
                      <div class="orders-mobile-card__summary-item">
                        <span class="label">Total</span>
                        <strong>{{ formatearMonto(pedido.total_neto) }}</strong>
                      </div>
                      <div class="orders-mobile-card__summary-item">
                        <span class="label">Acción sugerida</span>
                        <strong>{{ esCompletado(pedido.estado) ? 'Retomar' : 'Editar' }}</strong>
                      </div>
                    </div>

                    <div class="d-grid gap-2 mt-3">
                      <button
                        type="button"
                        class="btn btn-outline-primary btn-sm"
                        @click="verDetalle(pedido.pedido_id)"
                      >
                        Ver detalle
                      </button>
                      <button
                        v-if="esGestionableDesdePedidos(pedido.estado)"
                        type="button"
                        class="btn btn-outline-warning btn-sm"
                        @click="editarPedido(pedido)"
                      >
                        {{ esCompletado(pedido.estado) ? 'Retomar para Editar' : 'Editar Pedido' }}
                      </button>
                      <button
                        v-if="esCancelacionPermitida(pedido.estado)"
                        type="button"
                        class="btn btn-outline-danger btn-sm"
                        @click="cancelarPedidoDesdeLista(pedido)"
                      >
                        Cancelar Pedido
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="table-responsive d-none d-lg-block">
                <table class="table table-striped align-middle">
                  <thead>
                    <tr>
                      <th>ID Pedido</th>
                      <th>Cliente</th>
                      <th>Total Neto</th>
                      <th>Estado</th>
                      <th>Actualizado en</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="cargando">
                      <td colspan="6" class="text-center py-4">Cargando pedidos...</td>
                    </tr>
                    <tr v-else-if="!pedidos.length">
                      <td colspan="6" class="text-center py-4">No se encontraron pedidos.</td>
                    </tr>
                    <tr v-for="pedido in pedidos" :key="pedido.pedido_id">
                      <td>{{ pedido.pedido_id }}</td>
                      <td>{{ pedido.nombre_cliente || '—' }}</td>
                      <td>{{ formatearMonto(pedido.total_neto) }}</td>
                      <td>
                        <span :class="['badge', badgeClass(pedido.estado)]">
                          {{ pedido.estado || '—' }}
                        </span>
                      </td>
                      <td>{{ formatearFecha(pedido.actualizado_en) }}</td>
                      <td>
                        <div class="d-flex flex-wrap gap-2">
                          <button
                            type="button"
                            class="btn btn-outline-primary btn-sm"
                            @click="verDetalle(pedido.pedido_id)"
                          >
                            Ver
                          </button>
                          <button
                            v-if="esGestionableDesdePedidos(pedido.estado)"
                            type="button"
                            class="btn btn-outline-warning btn-sm"
                            @click="editarPedido(pedido)"
                          >
                            {{ esCompletado(pedido.estado) ? 'Retomar' : 'Editar' }}
                          </button>
                          <button
                            v-if="esCancelacionPermitida(pedido.estado)"
                            type="button"
                            class="btn btn-outline-danger btn-sm"
                            @click="cancelarPedidoDesdeLista(pedido)"
                          >
                            Cancelar
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <AppPagination
                :currentPage="paginaActual"
                :totalPages="totalPaginas"
                @page-changed="cambiarPagina"
              />
            </div>
          </div>
        </div>

        <div class="col-xl-5">
          <div v-if="panelLoading" class="card shadow-sm">
            <div class="card-body text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Cargando...</span>
              </div>
              <p class="mt-3 mb-0 text-muted">Preparando pedido...</p>
            </div>
          </div>

          <OrderEditorForm
            v-else
            :pedido="pedidoActivo"
            :readonly="panelReadonly"
            :saving="guardandoPedido"
            :allow-save-pending="allowSavePending"
            :allow-complete="allowComplete"
            :allow-cancel="allowCancel"
            :pending-label="pendingLabel"
            :complete-label="completeLabel"
            :cancel-label="cancelLabel"
            :title="panelTitle"
            :subtitle="panelSubtitle"
            @save-pending="guardarComoPendiente"
            @complete="completarPedido"
            @cancel-order="cancelarDesdePanel"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- RF-8b: con caja única efectiva, "Completar y Enviar a Caja" cobra en el
       acto sin cambiar de ruta. Con el modo apagado —que es el default— este
       modal no se monta y la pantalla se comporta igual que siempre. -->
  <CobroPedidoInlineModal
    :open="mostrarCobroInline"
    :movimiento-id="cobroInlineMovimientoId"
    :pedido-id="cobroInlinePedidoId"
    :total="cobroInlineTotal"
    :nombre-cliente="cobroInlineNombreCliente"
    :documento="cobroInlineDocumento"
    @cobrado="handleCobroInlineCobrado"
    @cerrado="handleCobroInlineCerrado"
  />
</template>

<script>
import AppNavbar from '../components/AppNavbar.vue';
import AppHeader from '../components/AppHeader.vue';
import AppFilter from '../components/AppFilter.vue';
import AppPagination from '../components/AppPagination.vue';
import OrderEditorForm from '@/components/orders/OrderEditorForm.vue';
import apiService from '../services/apiService.js';
import operationalConfigService from '../services/operationalConfigService.js';
import pedidoOnlineService from '../services/pedidoOnlineService.js';
import { hasPermission } from '../services/authSessionService.js';
import { useCashboxStore } from '@/stores/cashbox';
import CobroPedidoInlineModal from '@/components/CobroPedidoInlineModal.vue';
import localPrintSettingsService from '@/services/localPrintSettingsService';
import invoiceLocalPrintService from '@/services/invoiceLocalPrintService';

export default {
  name: 'GestionPedidos',
  components: {
    AppNavbar,
    AppHeader,
    AppFilter,
    AppPagination,
    CobroPedidoInlineModal,
    OrderEditorForm
  },
  data() {
    return {
      cashboxStore: useCashboxStore(),
      operationalConfig: operationalConfigService.getConfig(operationalConfigService.getCurrentSucursalId()),
      // Estado del cobro inline (RF-8b). Vive acá y no en el modal: el modal
      // sólo emite, la orquestación es de la pantalla.
      mostrarCobroInline: false,
      cobroInlinePedidoId: null,
      cobroInlineMovimientoId: null,
      cobroInlineTotal: 0,
      cobroInlineNombreCliente: '',
      cobroInlineDocumento: '',
      pedidos: [],
      searchInput: '',
      paginaActual: 1,
      itemsPorPagina: 10,
      totalPaginas: 1,
      totalPedidos: 0,
      cargando: false,
      panelLoading: false,
      guardandoPedido: false,
      pedidoActivo: null,
      panelMode: 'create',
      // Conteo de pedidos web pendientes (RF-7) — el detalle vive en
      // Pedidos > E-Pedidos (EPedidosView.vue), acá solo se muestra el
      // aviso con el link.
      pedidosWebPendientesCount: 0
    };
  },
  computed: {
    canManageEcommerce() {
      return hasPermission('gestionar_ecommerce');
    },
    currentSucursalId() {
      return operationalConfigService.getCurrentSucursalId();
    },
    // --- RF-8b: condición triple de caja única -----------------------------
    // Se reusa tal cual la definida en §2.2 de
    // docs/modules/ventas-cajero-unico-y-atajos.md, para que el modo signifique
    // exactamente lo mismo en Pedidos que en PantallaInicio.
    canCobrarEnCaja() {
      return hasPermission('cobrar_venta');
    },
    modoUnipersonalHabilitado() {
      return Boolean(this.operationalConfig?.modo_cajero_unico) && this.canCobrarEnCaja;
    },
    modoUnipersonalEfectivo() {
      return this.modoUnipersonalHabilitado
        && this.cashboxStore.isOpen
        && Number(this.cashboxStore.sucursalId) === Number(this.currentSucursalId);
    },
    pedidoActivoEstado() {
      return (this.pedidoActivo?.estado || '').toLowerCase();
    },
    panelReadonly() {
      return ['finalizado', 'cancelado'].includes(this.pedidoActivoEstado) || this.panelMode === 'readonly';
    },
    allowSavePending() {
      if (this.panelReadonly) return false;
      return true;
    },
    allowComplete() {
      if (this.panelReadonly) return false;
      return true;
    },
    allowCancel() {
      if (this.panelMode === 'create') return true;
      return ['pendiente', 'completado'].includes(this.pedidoActivoEstado);
    },
    pendingLabel() {
      if (this.panelMode === 'create') return 'Guardar Pendiente';
      return 'Guardar como Pendiente';
    },
    completeLabel() {
      // Mismo criterio que `labelBotonPrimario` de PantallaInicio: el texto
      // dice lo que el botón realmente va a hacer.
      return this.modoUnipersonalEfectivo ? 'Completar y cobrar' : 'Completar y Enviar a Caja';
    },
    cancelLabel() {
      if (this.panelMode === 'create') return 'Descartar';
      return 'Cancelar Pedido';
    },
    panelTitle() {
      if (this.panelMode === 'create') return 'Nuevo Pedido';
      if (!this.pedidoActivo?.pedido_id) return 'Pedido';
      return `Pedido #${this.pedidoActivo.pedido_id}`;
    },
    panelSubtitle() {
      if (this.panelMode === 'create') {
        return 'Gestiona la carga del pedido y decide si queda pendiente o si se completa para enviarlo a caja.';
      }
      if (this.panelReadonly) {
        if (this.pedidoActivoEstado === 'completado') {
          return 'Pedido completado y publicado para cobro en caja. Para volver a editarlo, usa Retomar desde la lista y regresará a pendiente.';
        }
        return 'Vista de solo lectura. El pedido no puede modificarse desde Gestión de Pedidos.';
      }
      return 'Puedes editar, dejar pendiente, completar para caja o cancelar desde esta misma pantalla.';
    }
  },
  watch: {
    async searchInput() {
      this.paginaActual = 1;
      await this.cargarPedidos({ page: 1 });
    }
  },
  async mounted() {
    await this.cargarPedidos();
    if (this.canManageEcommerce) {
      this.cargarPedidosWebPendientesCount();
    }
    // RF-8a: al venir de "Atender" en E-Pedidos, el pedido recién creado se
    // abre directamente en el panel. Sin el parámetro la pantalla se comporta
    // exactamente como antes.
    await this.abrirPedidoDesdeQuery();

    // RF-8b: el estado de caja sólo se consulta si el modo caja única está
    // habilitado en este puesto y el usuario puede cobrar. Con el modo apagado
    // —que es el default— esta pantalla no hace ninguna request nueva, que es
    // la garantía de no regresión §9.2.5 del SPEC.
    if (this.modoUnipersonalHabilitado) {
      await this.cashboxStore.fetchCurrentOpen({ sucursal_id: this.currentSucursalId });
    }
  },
  methods: {
    async abrirPedidoDesdeQuery() {
      const pedidoId = Number(this.$route?.query?.pedido_id);
      if (!Number.isInteger(pedidoId) || pedidoId <= 0) return;
      this.panelLoading = true;
      try {
        const detalle = await this.cargarPedidoDetalle(pedidoId);
        if (!detalle) return;
        this.pedidoActivo = detalle;
        this.panelMode = this.panelReadonly ? 'readonly' : 'edit';
      } catch (error) {
        // Un pedido inexistente o que no se puede cargar no rompe la pantalla:
        // el operador queda en la lista, que es un destino válido.
        console.error('No se pudo abrir el pedido indicado en la URL', error);
      } finally {
        this.panelLoading = false;
      }
    },
    async cargarPedidosWebPendientesCount() {
      try {
        this.pedidosWebPendientesCount = await pedidoOnlineService.countPendientes();
      } catch (error) {
        console.error('Error cargando el conteo de pedidos web pendientes', error);
        this.pedidosWebPendientesCount = 0;
      }
    },
    async cargarPedidos({ page = this.paginaActual } = {}) {
      this.cargando = true;
      try {
        const params = {
          limit: this.itemsPorPagina,
          page
        };
        const term = (this.searchInput || '').trim();
        if (term) params.search = term;

        const { data } = await apiService.get('/api/orders/pending', params);
        this.pedidos = Array.isArray(data?.pedidos) ? data.pedidos : [];
        this.paginaActual = Number(data?.pagina_actual || page) || 1;
        this.totalPaginas = Number(data?.total_paginas || 1) || 1;
        this.totalPedidos = Number(data?.total_pedidos || this.pedidos.length) || this.pedidos.length;
      } catch (error) {
        this.pedidos = [];
        this.totalPaginas = 1;
        this.totalPedidos = 0;
        if (error?.response?.status !== 404) {
          alert(error.userMessage || 'No se pudo cargar la lista de pedidos.');
        }
      } finally {
        this.cargando = false;
      }
    },
    async cambiarPagina(page) {
      if (page === this.paginaActual) return;
      this.paginaActual = page;
      await this.cargarPedidos({ page });
    },
    iniciarNuevoPedido() {
      this.panelMode = 'create';
      this.pedidoActivo = null;
    },
    esCompletado(estado) {
      return (estado || '').toLowerCase() === 'completado';
    },
    esGestionableDesdePedidos(estado) {
      const normalized = (estado || '').toLowerCase();
      return normalized === 'pendiente' || normalized === 'completado';
    },
    esCancelacionPermitida(estado) {
      const normalized = (estado || '').toLowerCase();
      return normalized === 'pendiente' || normalized === 'completado';
    },
    async cargarPedidoDetalle(pedidoId) {
      const { data } = await apiService.get(`/api/orders/pending/${pedidoId}`);
      return data;
    },
    async verDetalle(pedidoId) {
      this.panelLoading = true;
      try {
        this.pedidoActivo = await this.cargarPedidoDetalle(pedidoId);
        this.panelMode = 'readonly';
      } catch (error) {
        alert(error.userMessage || 'No se pudo cargar el detalle del pedido.');
      } finally {
        this.panelLoading = false;
      }
    },
    async editarPedido(pedido) {
      this.panelLoading = true;
      try {
        if (this.esCompletado(pedido.estado)) {
          await apiService.post(`/api/orders/pending/${pedido.pedido_id}/return-to-draft`, {});
        }
        this.pedidoActivo = await this.cargarPedidoDetalle(pedido.pedido_id);
        this.panelMode = 'edit';
        await this.cargarPedidos();
      } catch (error) {
        alert(error.userMessage || 'No se pudo preparar el pedido para edición.');
      } finally {
        this.panelLoading = false;
      }
    },
    async guardarComoPendiente(payload) {
      this.guardandoPedido = true;
      try {
        if (this.panelMode === 'create' || !this.pedidoActivo?.pedido_id) {
          const { data } = await apiService.post('/api/orders/pending', {
            ...payload,
            cabecera: {
              ...(payload?.cabecera || {}),
              sucursal_id: this.currentSucursalId
            },
            confirmar: false
          });
          const pedidoId = data?.data?.pedido_id;
          alert(pedidoId ? `Pedido #${pedidoId} guardado como pendiente.` : 'Pedido guardado como pendiente.');
          if (pedidoId) {
            this.pedidoActivo = await this.cargarPedidoDetalle(pedidoId);
            this.panelMode = 'edit';
          } else {
            this.iniciarNuevoPedido();
          }
        } else {
          await apiService.post(`/api/orders/pending/${this.pedidoActivo.pedido_id}`, {
            ...payload,
            cabecera: {
              ...(payload?.cabecera || {}),
              sucursal_id: this.currentSucursalId
            },
            confirmar: false
          });
          this.pedidoActivo = await this.cargarPedidoDetalle(this.pedidoActivo.pedido_id);
          this.panelMode = 'edit';
          alert(`Pedido #${this.pedidoActivo.pedido_id} actualizado como pendiente.`);
        }
        await this.cargarPedidos();
      } catch (error) {
        alert(error.userMessage || 'No se pudo guardar el pedido como pendiente.');
      } finally {
        this.guardandoPedido = false;
      }
    },
    async completarPedido(payload, meta = {}) {
      this.guardandoPedido = true;
      try {
        let pedidoId = null;
        let movimientoId = null;

        if (this.panelMode === 'create' || !this.pedidoActivo?.pedido_id) {
          const { data } = await apiService.post('/api/orders/pending', {
            ...payload,
            cabecera: {
              ...(payload?.cabecera || {}),
              sucursal_id: this.currentSucursalId
            },
            confirmar: true
          });
          pedidoId = data?.data?.pedido_id;
          movimientoId = data?.data?.caja_movimiento_id;
          if (pedidoId) {
            this.pedidoActivo = await this.cargarPedidoDetalle(pedidoId);
            this.panelMode = 'readonly';
          }
        } else {
          const { data } = await apiService.post(`/api/orders/pending/${this.pedidoActivo.pedido_id}`, {
            ...payload,
            cabecera: {
              ...(payload?.cabecera || {}),
              sucursal_id: this.currentSucursalId
            },
            confirmar: true
          });
          pedidoId = this.pedidoActivo.pedido_id;
          movimientoId = data?.data?.caja_movimiento_id;
          this.pedidoActivo = await this.cargarPedidoDetalle(pedidoId);
          this.panelMode = 'readonly';
        }

        await this.cargarPedidos();

        // A esta altura el pedido YA está publicado en caja. Todo lo que sigue
        // puede fallar sin perderlo: si el cobro inline no abre, se cancela o
        // se cierra, el pedido sigue en el backlog compartido de
        // /cobro-factura-venta para que lo cobre quien corresponda.
        if (this.modoUnipersonalEfectivo) {
          if (!movimientoId) {
            alert(
              pedidoId
                ? `Pedido #${pedidoId} enviado a caja. No se pudo abrir el cobro: cobralo desde Caja › Cobrar pedidos.`
                : 'Pedido enviado a caja. No se pudo abrir el cobro: cobralo desde Caja › Cobrar pedidos.'
            );
            return;
          }
          this.cobroInlinePedidoId = pedidoId;
          this.cobroInlineMovimientoId = movimientoId;
          this.cobroInlineTotal = Number(meta?.total || this.totalDelPedidoActivo());
          this.cobroInlineNombreCliente = meta?.nombreCliente || this.pedidoActivo?.nombre_cliente || '';
          // P2 RN-VI1: decide si se ofrece el ticket.
          this.cobroInlineDocumento = payload?.cabecera?.nro_documento || this.pedidoActivo?.nro_documento || '';
          this.mostrarCobroInline = true;
          return;
        }

        alert(pedidoId
          ? `Pedido #${pedidoId} completado y enviado a caja.`
          : 'Pedido completado y enviado a caja.');
      } catch (error) {
        alert(error.userMessage || 'No se pudo completar el pedido.');
      } finally {
        this.guardandoPedido = false;
      }
    },
    // Respaldo por si el editor no mandó el total (ej. una confirmación que no
    // nace del botón del formulario): se recalcula del detalle ya cargado.
    totalDelPedidoActivo() {
      const detalles = this.pedidoActivo?.detalles || [];
      return Math.round(detalles.reduce(
        (acc, d) => acc + (Number(d.cantidad) || 0) * (Number(d.precio) || 0), 0
      ));
    },
    async handleCobroInlineCobrado({ id_venta, nro_factura, vuelto, fiscal = null, printJob = null }) {
      const pedidoId = this.cobroInlinePedidoId;
      const movimientoId = this.cobroInlineMovimientoId;

      alert(vuelto > 0
        ? `Cobro registrado. Vuelto: ${Number(vuelto).toLocaleString('es-PY')} ₲`
        : 'Cobro registrado correctamente.');

      this.cerrarEstadoCobroInline();

      if (id_venta > 0) {
        // Un fallo de impresión nunca bloquea el refresco: la venta ya está
        // registrada y reimprimir es recuperable.
        try {
          await this.maybePrintInvoice({
            saleId: id_venta,
            movimientoId,
            invoiceNumber: nro_factura || `PEDIDO-${pedidoId}`,
            fiscal,
            printJob
          });
        } catch (error) {
          console.error('No se pudo imprimir el comprobante del cobro inline', error);
        }
      }

      await this.cargarPedidos();
      await this.cashboxStore.fetchCurrentOpen({ sucursal_id: this.currentSucursalId });
    },
    handleCobroInlineCerrado() {
      const pedidoId = this.cobroInlinePedidoId;
      this.cerrarEstadoCobroInline();
      alert(pedidoId
        ? `El pedido #${pedidoId} quedó pendiente de cobro en caja.`
        : 'El pedido quedó pendiente de cobro en caja.');
    },
    cerrarEstadoCobroInline() {
      this.mostrarCobroInline = false;
      this.cobroInlinePedidoId = null;
      this.cobroInlineMovimientoId = null;
      this.cobroInlineTotal = 0;
      this.cobroInlineNombreCliente = '';
      this.cobroInlineDocumento = '';
    },
    // Copia fiel de `maybePrintInvoice` de PantallaInicio: mismos nombres de
    // settings (`enabled` / `invoicePrintMode`) y misma llamada
    // (`printInvoiceBySale`). Divergir acá haría que la impresión se comporte
    // distinto según desde qué pantalla se cobró.
    async maybePrintInvoice({ saleId, movimientoId, invoiceNumber = '', fiscal = null, printJob = null }) {
      const settings = localPrintSettingsService.getSettings();
      // P2 RN-VI5: cobrada con ticket, no hay factura.
      const sinFactura = fiscal?.comprobante === 'TICKET';
      if (!settings.enabled || settings.invoicePrintMode === 'disabled') return;

      if (settings.invoicePrintMode === 'confirm') {
        const confirmado = await window.confirm(
          `${sinFactura ? 'El ticket' : 'La factura'} ${invoiceNumber || saleId} ${sinFactura ? 'está listo' : 'está lista'}. ¿Deseas enviarl${sinFactura ? 'o' : 'a'} ahora a la impresora local?`,
          { title: sinFactura ? 'Imprimir ticket' : 'Imprimir factura', confirmText: 'Imprimir', cancelText: 'Omitir' }
        );
        if (!confirmado) return;
      }

      const result = await invoiceLocalPrintService.printInvoiceBySale({
        saleId,
        movimientoId,
        settings,
        printJob,
        sinFactura
      });
      if (sinFactura) {
        window.alert.info('Ticket enviado al agente local. La venta queda pendiente de factura.');
        return;
      }
      if (result?.mode === 'offline_contingency' || fiscal?.status === 'OFFLINE_PENDING_SYNC') {
        window.alert.info('Comprobante temporal enviado al agente local. Queda pendiente de validación fiscal.');
      }
    },
    async cancelarDesdePanel() {
      if (this.panelMode === 'create' || !this.pedidoActivo?.pedido_id) {
        this.iniciarNuevoPedido();
        return;
      }
      await this.cancelarPedido(this.pedidoActivo);
    },
    async cancelarPedidoDesdeLista(pedido) {
      await this.cancelarPedido(pedido);
    },
    async cancelarPedido(pedido) {
      const confirmed = await confirm({
        message: `¿Deseas cancelar el pedido #${pedido.pedido_id}?`,
        title: 'Cancelar pedido',
        confirmText: 'Cancelar pedido',
        cancelText: 'Mantener'
      });
      if (!confirmed) return;

      this.guardandoPedido = true;
      try {
        await apiService.post(`/api/orders/pending/${pedido.pedido_id}/cancel`, {});
        alert(`Pedido #${pedido.pedido_id} cancelado correctamente.`);
        if (this.pedidoActivo?.pedido_id === pedido.pedido_id) {
          this.pedidoActivo = await this.cargarPedidoDetalle(pedido.pedido_id);
          this.panelMode = 'readonly';
        }
        await this.cargarPedidos();
      } catch (error) {
        alert(error.userMessage || 'No se pudo cancelar el pedido.');
      } finally {
        this.guardandoPedido = false;
      }
    },
    formatearMonto(monto) {
      const numero = Number(monto);
      if (!Number.isFinite(numero)) return '0';
      return `${Math.round(numero).toLocaleString('es-PY', { minimumFractionDigits: 0 })} ₲`;
    },
    formatearFecha(valor) {
      if (!valor) return '—';
      const fecha = new Date(valor);
      if (Number.isNaN(fecha.getTime())) return '—';
      return `${fecha.toLocaleDateString('es-PY')} ${fecha.toLocaleTimeString('es-PY', {
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    },
    badgeClass(estado) {
      const normalized = (estado || '').toLowerCase();
      if (normalized === 'pendiente') return 'bg-warning text-dark';
      if (normalized === 'completado') return 'bg-info text-dark';
      if (normalized === 'finalizado') return 'bg-success';
      if (normalized === 'cancelado' || normalized === 'anulado') return 'bg-danger';
      return 'bg-secondary';
    }
  }
};
</script>

<style scoped>
.orders-mobile-context {
  background: linear-gradient(135deg, #f3efe4 0%, #ffffff 100%);
  border: 1px solid rgba(166, 111, 36, 0.12);
}

.orders-mobile-context__eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #8c5d1d;
  margin-bottom: 0.35rem;
}

.orders-mobile-context__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
}

.orders-mobile-context__subtitle {
  margin-top: 0.35rem;
  font-size: 0.92rem;
  color: #6b7280;
}

.orders-mobile-list {
  display: grid;
  gap: 0.75rem;
}

.orders-mobile-card {
  border-radius: 16px;
}

.orders-mobile-card__eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #8c5d1d;
}

.orders-mobile-card__title {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
}

.orders-mobile-card__meta {
  font-size: 0.88rem;
  color: #6b7280;
}

.orders-mobile-card__summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.orders-mobile-card__summary-item {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.75rem;
}

.orders-mobile-card__summary-item .label {
  display: block;
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin-bottom: 0.2rem;
}

@media (max-width: 575.98px) {
  .orders-mobile-card__summary {
    grid-template-columns: 1fr;
  }
}
</style>
